import { supabase, handleSupabaseError } from './config';

/**
 * Mendaftar pengguna baru dengan email dan password
 * @param {string} email - Email pengguna
 * @param {string} password - Password pengguna 
 * @param {object} additionalData - Data tambahan seperti nama, role, dll
 * @returns {Promise<object>} User object
 */
export const signUpWithEmailAndPassword = async (email, password, additionalData = {}) => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: additionalData
      }
    });

    if (error) throw error;
    return data.user;
  } catch (error) {
    console.error('Error signing up:', error);
    handleSupabaseError(error);
  }
};

/**
 * Login pengguna dengan email dan password
 * @param {string} email - Email pengguna
 * @param {string} password - Password pengguna
 * @returns {Promise<object>} User object
 */
export const signInWithEmailAndPassword = async (email, password) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;
    return data.user;
  } catch (error) {
    console.error('Error signing in:', error);
    handleSupabaseError(error);
  }
};

/**
 * Logout pengguna
 * @returns {Promise<void>}
 */
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  } catch (error) {
    console.error('Error signing out:', error);
    handleSupabaseError(error);
  }
};

/**
 * Reset password pengguna
 * @param {string} email - Email pengguna
 * @returns {Promise<void>}
 */
export const sendPasswordResetEmail = async (email) => {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    
    if (error) throw error;
  } catch (error) {
    console.error('Error sending password reset:', error);
    handleSupabaseError(error);
  }
};

/**
 * Update password pengguna
 * @param {string} password - Password baru
 * @returns {Promise<void>}
 */
export const updatePassword = async (password) => {
  try {
    const { error } = await supabase.auth.updateUser({
      password
    });
    
    if (error) throw error;
  } catch (error) {
    console.error('Error updating password:', error);
    handleSupabaseError(error);
  }
};

/**
 * Mendapatkan user saat ini
 * @returns {object|null} Current user atau null
 */
export const getCurrentUser = () => {
  return supabase.auth.getUser();
};

/**
 * Auth state listener - mendengarkan perubahan status auth
 * @param {function} callback - Callback function yang dipanggil saat auth state berubah
 * @returns {function} Unsubscribe function
 */
export const authStateListener = (callback) => {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(
    async (event, session) => {
      console.log('Supabase auth state change:', event, session?.user?.id);
      
      if (session?.user) {
        // Transformasi user object agar kompatibel dengan Firebase format
        const user = {
          uid: session.user.id, // Firebase menggunakan uid, Supabase menggunakan id
          id: session.user.id,
          email: session.user.email,
          emailVerified: session.user.email_confirmed_at != null,
          ...session.user.user_metadata
        };
        callback(user);
      } else {
        callback(null);
      }
    }
  );

  // Return unsubscribe function
  return () => subscription.unsubscribe();
};

/**
 * Mendapatkan session saat ini
 * @returns {Promise<object|null>} Current session atau null
 */
export const getCurrentSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  } catch (error) {
    console.error('Error getting current session:', error);
    return null;
  }
};

/**
 * Membuat user mitra baru (alias untuk signUpWithEmailAndPassword)
 * @param {string} email - Email mitra
 * @param {string} password - Password mitra
 * @param {object} additionalData - Data tambahan
 * @returns {Promise<object>} User object with user property for compatibility
 */
export const createNewMitraUser = async (email, password, additionalData = {}) => {
  try {
    const user = await signUpWithEmailAndPassword(email, password, additionalData);
    // Return in format compatible with Firebase
    return { user };
  } catch (error) {
    console.error('Error creating new mitra user:', error);
    handleSupabaseError(error);
  }
};
